import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import '../style/Home.css';
import { IoSend, IoCheckmark, IoArrowBack } from "react-icons/io5";

import Avatar from '../components/Avatar';
import { useProfile } from '../profile/ProfileContext';
import { presenceClass, presenceLabelKey } from '../utils/presence';
import { getFriends } from '../api/FriendsAPI';
import { sendMessage, showConversation, showLastMessages } from '../api/ChatAPI';
import { useSocket } from '../socket/SocketContext';
import { useLanguage } from '../i18n/LanguageContext';
import { linkify } from '../utils/linkify';

const username = () => localStorage.getItem('username');
const token = () => localStorage.getItem('accessToken');


const formatTime = (iso, locale) => new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

const formatDay = (iso, locale, t) => {
  const date = new Date(iso);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return t('chat.today');
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return t('chat.yesterday');
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
};

export default function Chat() {
  const { socket, markConversationRead, unreadMessageCounts, pushToast, statusOf, connected } = useSocket();
  const { t, locale } = useLanguage();
  const { openProfile } = useProfile();
  const history = useHistory();
  const location = useLocation();

  const [friends, setFriends] = useState([]);
  const [lastMessages, setLastMessages] = useState({}); // username -> {lastMessage, updatedAt}
  const [selected, setSelected] = useState(null); // friend object
  const [messages, setMessages] = useState([]); // [{senderId, msg, createdAt}] oldest -> newest
  const [message, setMessage] = useState("");
  const [hasMore, setHasMore] = useState(true);

  const selectedRef = useRef(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const chatRef = useRef(null);
  const stickToBottomRef = useRef(null); // null | 'instant' | 'smooth'
  useEffect(() => {
    if (stickToBottomRef.current && chatRef.current) {
      chatRef.current.scrollTo({ top: chatRef.current.scrollHeight, behavior: stickToBottomRef.current });
      stickToBottomRef.current = null;
    }
  }, [messages]);

  useEffect(() => {
    (async () => {
      const friendsRes = await getFriends(username(), token());
      if (friendsRes.status === 'success') setFriends(friendsRes.data);

      const lastRes = await showLastMessages(username(), token());
      if (lastRes.status === 'success') {
        const map = {};
        lastRes.conversations.forEach((c) => {
          const other = c.senderId === username() ? c.receiverId : c.senderId;
          map[other] = { lastMessage: c.lastMessage, updatedAt: c.updatedAt };
        });
        setLastMessages(map);
      }
    })();
  }, []);

  useEffect(() => {
    const socketInstance = socket && socket.current;
    if (!socketInstance) return;
    const onGetMessage = ({ senderId, text }) => {
      setLastMessages((prev) => ({ ...prev, [senderId]: { lastMessage: text, updatedAt: new Date().toISOString() } }));
      if (selectedRef.current && selectedRef.current.username === senderId) {
        stickToBottomRef.current = 'smooth';
        setMessages((prev) => [...prev, { senderId, msg: text, createdAt: new Date().toISOString(), live: true }]);
        markConversationRead(senderId);
      }
    };
    socketInstance.on('getMessage', onGetMessage);
    return () => socketInstance.off('getMessage', onGetMessage);
  // `connected`: see Friends.js - the socket may not exist yet on first mount.
  }, [socket, markConversationRead, connected]);

  const openConversation = async (friend) => {
    setSelected(friend);
    setMessages([]);
    setHasMore(true);
    markConversationRead(friend.username);
    const res = await showConversation(username(), token(), friend.username);
    if (res.status === 'success') {
      const ordered = res.message.slice().reverse().map(([senderId, msg, createdAt]) => ({ senderId, msg, createdAt }));
      stickToBottomRef.current = 'instant';
      setMessages(ordered);
      setHasMore(res.message.length === 10);
    }
  };

  // Search.js/Friends.js's "message" buttons land here via
  // history.push('/home/', {openChatWith: {...}}) - conversation selection
  // is local state (`selected` above), not a route param, so this is the
  // only way to deep-link into a specific DM from another page.
  // openConversation only ever touches friend.username/imageUrl/fullName,
  // so the object those pages already have on hand works as-is without
  // needing to cross-reference it against `friends` first. The state is
  // cleared right after (history.replace) so navigating back here later
  // (browser back, or just clicking the Chats tab again) doesn't
  // re-trigger it.
  useEffect(() => {
    const target = location.state && location.state.openChatWith;
    if (!target) return;
    openConversation(target);
    history.replace('/home/');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const loadOlder = async () => {
    if (!selected || !hasMore || messages.length === 0) return;
    const oldest = messages[0].createdAt;
    const res = await showConversation(username(), token(), selected.username, oldest);
    if (res.status === 'success') {
      const older = res.message.slice().reverse().map(([senderId, msg, createdAt]) => ({ senderId, msg, createdAt }));
      setMessages((prev) => [...older, ...prev]);
      setHasMore(res.message.length === 10);
    }
  };

  const onScroll = (e) => {
    if (e.target.scrollTop === 0) loadOlder();
  };

  const send = async () => {
    if (!message.trim() || !selected) return;
    const text = message;
    setMessage("");
    // A network error (offline, server down) throws instead of returning
    // a fail status - same handling as a refused send below.
    const res = await sendMessage(username(), token(), selected.username, text)
      .catch(() => ({ status: 'fail' }));
    if (res.status === 'success') {
      stickToBottomRef.current = 'smooth';
      setMessages((prev) => [...prev, { senderId: username(), msg: text, createdAt: new Date().toISOString(), live: true }]);
      setLastMessages((prev) => ({ ...prev, [selected.username]: { lastMessage: text, updatedAt: new Date().toISOString() } }));
      // No socket relay from here - the server delivers it to the receiver
      // once stored (chatController.js).
    } else {
      // Blocked (either way), receiver gone, offline... - put the text
      // back so it isn't silently lost, and say it didn't go through.
      setMessage(text);
      pushToast(t('chat.sendFailed'));
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') send();
  };

  const orderedFriends = friends.slice().sort((a, b) => {
    const ta = lastMessages[a.username] ? new Date(lastMessages[a.username].updatedAt).getTime() : 0;
    const tb = lastMessages[b.username] ? new Date(lastMessages[b.username].updatedAt).getTime() : 0;
    return tb - ta;
  });

  return (
    <div className={selected ? "SplitPage HasSelection" : "SplitPage"}>
        <div className="ChannelContainer glass">
            <h4>{t('chat.title')}</h4>
            <p className="descGray">{t('chat.subtitle')}</p>
            <ul className="Channels">
              {orderedFriends.length === 0 && <p className="descGray">{t('chat.emptyFriends')}</p>}
              {orderedFriends.map((friend) => {
                const last = lastMessages[friend.username];
                const unread = unreadMessageCounts[friend.username] || 0;
                return (
                  <li key={friend.username} onClick={() => openConversation(friend)} className={selected && selected.username === friend.username ? "ChActive glassCard" : "glassCard"}>
                    <Avatar imageUrl={friend.imageUrl} alt={friend.username} />
                    <div className="ChCon">
                      <div className="ChTopRow">
                        <b>{friend.fullName || friend.username}</b>
                        {last && <span className="ChTime">{formatTime(last.updatedAt, locale)}</span>}
                      </div>
                      <div className="ChBottomRow">
                        <aside className="descGray">{last ? last.lastMessage : t('chat.sayHi')}</aside>
                        {unread > 0 && <span className="ChBadge">{unread > 9 ? '9+' : unread}</span>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
        </div>
        <div className="ChatContainer">
          {selected ? (
            <>
              <div className="ChatHeader glass">
                <button className="MobileBackBtn NaviButton" onClick={() => setSelected(null)} title={t('common.back')}><IoArrowBack /></button>
                <div className="UserPP">
                  <Avatar imageUrl={selected.imageUrl} className={presenceClass(statusOf(selected.username))} alt={selected.username} username={selected.username} seed={selected} />
                </div>
                <div className="UserDetails AvatarClickable" onClick={(e) => openProfile(selected.username, e.currentTarget, selected)}>
                  {selected.fullName || selected.username}
                  <aside className="descGray">{t(presenceLabelKey(statusOf(selected.username)))}</aside>
                </div>
              </div>
              <div className="Chat" ref={chatRef} onScroll={onScroll}>
                {messages.map((m, i) => {
                  const dayLabel = formatDay(m.createdAt, locale, t);
                  const prevDayLabel = i > 0 ? formatDay(messages[i - 1].createdAt, locale, t) : null;
                  const own = m.senderId === username();
                  return (
                    <React.Fragment key={`${m.senderId}-${m.createdAt}`}>
                      {dayLabel !== prevDayLabel && <div className="DateDivider"><span>{dayLabel}</span></div>}
                      <div className={own ? "MessageContainer Own" : "MessageContainer Other"}>
                        <div className={m.live ? "MessageBox MessageBubbleIn" : "MessageBox"}>
                          <div className="MessageText">{linkify(m.msg)}</div>
                          <div className="MessageMeta">
                            {formatTime(m.createdAt, locale)}
                            {own && <IoCheckmark className="MessageCheck" />}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="ChatInput glass">
                <input type="text" placeholder={t('chat.inputPlaceholder')} value={message} autoComplete="off" onChange={(e) => { setMessage(e.target.value); }} onKeyDown={onKeyDown}></input>
                <button onClick={send} className="SendButton primaryColor"><IoSend/></button>
              </div>
            </>
          ) : (
            <div className="ChatHeader glass">
              <div className="UserDetails descGray">{t('chat.selectFriend')}</div>
            </div>
          )}
        </div>
    </div>
  );
}
